// src/components/tutorials/JS_Bitwise_Operators.js
import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };
  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Bitwise_Operators() {
  const bitwiseOps = [
    {
      op: "&",
      name: "AND",
      desc: "Sets each bit to 1 if both bits are 1",
      example: "5 & 1 → 1",
    },
    {
      op: "|",
      name: "OR",
      desc: "Sets each bit to 1 if one of two bits is 1",
      example: "5 | 1 → 5",
    },
    {
      op: "^",
      name: "XOR",
      desc: "Sets each bit to 1 if only one of two bits is 1",
      example: "5 ^ 1 → 4",
    },
    {
      op: "~",
      name: "NOT",
      desc: "Inverts all bits (1 → 0 and 0 → 1)",
      example: "~5 → -6",
    },
    {
      op: "<<",
      name: "Left Shift",
      desc: "Shifts bits to the left, adding zeros on the right",
      example: "5 << 1 → 10",
    },
    {
      op: ">>",
      name: "Right Shift",
      desc: "Shifts bits to the right, keeping the sign bit",
      example: "5 >> 1 → 2",
    },
    {
      op: ">>>",
      name: "Unsigned Right Shift",
      desc: "Shifts bits to the right, filling zeros on the left",
      example: "5 >>> 1 → 2",
    },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚙️ JavaScript Bitwise Operators</h1>
      <p className="tutorial-intro">Revised July 2025</p>

      {/* 🔹 Intro Section */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          color: "#065f46",
          borderRadius: "12px",
          padding: "1rem 1.5rem",
          marginBottom: "1.5rem",
        }}
      >
        <h2
          style={{
            color: "#065f46",
            fontSize: "1.8rem",
            marginBottom: "0.5rem",
          }}
        >
          What are Bitwise Operators?
        </h2>
        <p style={{ fontSize: "1.05rem" }}>
          Bitwise operators treat their operands as a sequence of 32 bits
          (binary digits) rather than as decimal, hexadecimal, or octal numbers.
        </p>
      </div>

      {/* 🔹 Bitwise Operators Table */}
      <div
        className="reference-table-container"
        style={{
          marginTop: "1rem",
          overflowX: "auto",
          borderRadius: "10px",
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        }}
      >
        <table
          className="reference-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1rem",
            backgroundColor: "#fff",
          }}
        >
          <thead style={{ background: "#0ea5e9", color: "white" }}>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.8rem 1rem",
                  width: "10%",
                }}
              >
                Operator
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.8rem 1rem",
                  width: "20%",
                }}
              >
                Name
              </th>
              <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                Description
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.8rem 1rem",
                  width: "20%",
                }}
              >
                Example
              </th>
            </tr>
          </thead>
          <tbody>
            {bitwiseOps.map((item, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9fafb" : "#fff",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <td
                  style={{
                    padding: "0.7rem 1rem",
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    color: "#2563eb",
                  }}
                >
                  {item.op}
                </td>
                <td style={{ padding: "0.7rem 1rem", fontWeight: 500 }}>
                  {item.name}
                </td>
                <td style={{ padding: "0.7rem 1rem" }}>{item.desc}</td>
                <td
                  style={{
                    padding: "0.7rem 1rem",
                    fontFamily: "monospace",
                    color: "#0d9488",
                    fontWeight: "bold",
                  }}
                >
                  {item.example}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Example 1: AND, OR, XOR */}
      <div className="tutorial-section" style={{ marginTop: "40px" }}>
        <h2 className="section-title">1️⃣ AND, OR, XOR</h2>
        <p>
          These operators compare bits at each position of two numbers and
          return a new value based on the bitwise rule.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 5;    // 0101
let b = 1;    // 0001
console.log(a & b); // AND → 0001 → 1
console.log(a | b); // OR  → 0101 → 5
console.log(a ^ b); // XOR → 0100 → 4`}
          />
          <TryItButton
            code={`let a = 5;
let b = 1;
console.log(a & b);
console.log(a | b);
console.log(a ^ b);`}
          />
        </div>
      </div>

      {/* Example 2: Bitwise NOT */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Bitwise NOT (~)</h2>
        <p>
          The NOT operator inverts each bit of the number — converting 1 to 0
          and 0 to 1. It’s equivalent to <code>-(x + 1)</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 5; // binary: 0000000000000101
console.log(~x); // → -6`}
          />
          <TryItButton code={`let x = 5; console.log(~x);`} />
        </div>
      </div>

      {/* Example 3: Shift Operators */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Shift Operators</h2>
        <p>
          Bitwise shifts move bits to the left or right. Left shift multiplies
          by powers of 2, and right shift divides by powers of 2.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let n = 5;      // binary: 0101
console.log(n << 1);  // Left shift: 10
console.log(n >> 1);  // Right shift: 2`}
          />
          <TryItButton
            code={`let n = 5;
console.log(n << 1);
console.log(n >> 1);`}
          />
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#0369a1" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Use bitwise operators for performance optimization in logic-heavy
            tasks.
          </li>
          <li>
            Remember: bitwise NOT (~x) equals <code>-(x + 1)</code>.
          </li>
          <li>Always test with small numbers to visualize bit changes.</li>
          <li>
            Bitwise operations only work on 32-bit integers, not floating
            numbers.
          </li>
        </ul>
      </div>

      {/* 🔹 Example Explanation Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>💡 Example Explanation</h2>
        <p style={{ marginBottom: "0.8rem" }}>
          Let's take two numbers: <code>5</code> (binary <code>0101</code>) and{" "}
          <code>1</code> (binary <code>0001</code>).
        </p>
        <ul className="tutorial-list-points">
          <li>
            <code>5 & 1</code> → <b>AND</b> → <code>0101 & 0001 = 0001</code> →{" "}
            <b>1</b>
          </li>
          <li>
            <code>5 | 1</code> → <b>OR</b> → <code>0101 | 0001 = 0101</code> →{" "}
            <b>5</b>
          </li>
          <li>
            <code>5 ^ 1</code> → <b>XOR</b> → <code>0101 ^ 0001 = 0100</code> →{" "}
            <b>4</b>
          </li>
          <li>
            <code>~5</code> → <b>NOT</b> → inverts all bits → <b>-6</b>
          </li>
          <li>
            <code>5 &lt;&lt; 1</code> → <b>Left Shift</b> → <code>1010</code> →{" "}
            <b>10</b>
          </li>
          <li>
            <code>5 &gt;&gt; 1</code> → <b>Right Shift</b> → <code>0010</code> →{" "}
            <b>2</b>
          </li>
        </ul>
      </div>

      {/* 🔹 Notes Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🧠 Notes</h2>
        <ul className="tutorial-list-points">
          <li>
            Bitwise operations convert numbers to 32-bit signed integers
            internally.
          </li>
          <li>These operators are faster but work only at the bit level.</li>
          <li>
            Useful in low-level programming, masks, encryption, and compression
            logic.
          </li>
        </ul>
      </div>
    </div>
  );
}
